import { authenticatedFetch } from '../api';

export type RegistrationFormSettings = {
  require_vat: boolean;
  allowed_countries: string[];
  business_types: string[];
  custom_fields: Array<{
    key: string;
    label: string;
    field_type: 'text' | 'number' | 'select' | 'checkbox';
    required: boolean;
    help_text: string | null;
    options: string[];
  }>;
  privacy_notice_url: string | null;
  privacy_notice_version: string;
  consent_text: string;
};

export type RegistrationForm = {
  id: string;
  public_key: string;
  name: string;
  title: string;
  description: string | null;
  status: 'draft' | 'active' | 'disabled';
  settings: RegistrationFormSettings;
  public_path: string;
  created_at: string;
  updated_at: string;
};

export type RegistrationApplication = {
  id: string;
  form_id: string;
  status:
    | 'submitted'
    | 'under_review'
    | 'needs_information'
    | 'approved'
    | 'rejected'
    | 'provisioning'
    | 'provisioned'
    | 'failed';
  company: {
    name: string;
    business_type: string | null;
    website: string | null;
    country_code: string;
    address1: string | null;
    address2: string | null;
    city: string | null;
    province: string | null;
    postal_code: string | null;
  };
  contact: {
    first_name: string;
    last_name: string;
    email: string;
    phone: string | null;
  };
  vat: {
    country_code: string | null;
    number: string | null;
    status: string;
    provider: string | null;
    business_name: string | null;
    business_address: string | null;
    reference: string | null;
    verified_at: string | null;
    evidence: Record<string, unknown>;
  };
  custom_fields: Record<string, unknown>;
  applicant_note: string | null;
  review: {
    note: string | null;
    reviewed_by: string | null;
    reviewed_at: string | null;
  };
  shopify: {
    company_id: string | null;
    company_location_id: string | null;
  };
  commercial_defaults: Record<string, unknown>;
  submitted_at: string;
  updated_at: string;
  warnings?: string[];
  provisioning_ready?: boolean;
};

export type RegistrationFormInput = {
  name: string;
  title: string;
  description?: string | null;
  status: 'draft' | 'active' | 'disabled';
  settings: RegistrationFormSettings;
};

async function jsonOrError<T>(response: Response, fallback: string): Promise<T> {
  if (!response.ok) {
    const body = (await response.json().catch(() => null)) as { detail?: string } | null;
    throw new Error(body?.detail ?? `${fallback} (${response.status})`);
  }
  return (await response.json()) as T;
}

export async function listRegistrationForms(): Promise<RegistrationForm[]> {
  const response = await authenticatedFetch('/api/registration/forms');
  const body = await jsonOrError<{ forms: RegistrationForm[] }>(
    response,
    'Could not load registration forms',
  );
  return body.forms;
}

export async function createRegistrationForm(
  input: RegistrationFormInput,
): Promise<RegistrationForm> {
  const response = await authenticatedFetch('/api/registration/forms', {
    method: 'POST',
    body: JSON.stringify(input),
  });
  return jsonOrError(response, 'Could not create registration form');
}

export async function updateRegistrationForm(
  formId: string,
  input: RegistrationFormInput,
): Promise<RegistrationForm> {
  const response = await authenticatedFetch(`/api/registration/forms/${formId}`, {
    method: 'PUT',
    body: JSON.stringify(input),
  });
  return jsonOrError(response, 'Could not update registration form');
}

export async function listRegistrationApplications(): Promise<RegistrationApplication[]> {
  const response = await authenticatedFetch('/api/registration/applications?limit=100');
  const body = await jsonOrError<{ applications: RegistrationApplication[] }>(
    response,
    'Could not load registration applications',
  );
  return body.applications;
}

export async function verifyRegistrationVat(
  applicationId: string,
): Promise<RegistrationApplication> {
  const response = await authenticatedFetch(
    `/api/registration/applications/${applicationId}/verify-vat`,
    { method: 'POST' },
  );
  return jsonOrError(response, 'Could not verify VAT number');
}

export async function reviewRegistrationApplication(
  applicationId: string,
  status: 'under_review' | 'needs_information' | 'approved' | 'rejected',
  reviewerNote?: string,
): Promise<RegistrationApplication> {
  const response = await authenticatedFetch(
    `/api/registration/applications/${applicationId}/review`,
    {
      method: 'PATCH',
      body: JSON.stringify({ status, reviewer_note: reviewerNote || null }),
    },
  );
  return jsonOrError(response, 'Could not review registration application');
}
